import { isObject } from './guards';

export const keys = <T extends object>(value: T): (keyof T)[] =>
  Object.keys(value ?? {}) as (keyof T)[];

export const values = <T extends object>(value: T): T[keyof T][] =>
  Object.values(value ?? {}) as T[keyof T][];

export const mapObject = <T extends object, R>(
  value: T,
  callback: (value: T[keyof T], key: keyof T) => R,
  filter?: (value: R, key: keyof T) => boolean
): { [P in keyof T]: R } =>
  keys(value).reduce((result, key) => {
    const mapped = callback(value[key], key);

    if (filter && !filter(mapped, key)) return result;

    return { ...result, [key]: mapped };
  }, {} as { [P in keyof T]: R });

type ValueOfKey<T, K extends PropertyKey> = T extends unknown
  ? K extends keyof T
    ? T[K]
    : undefined
  : never;

export const maybeGet = <T, K extends PropertyKey>(value: T, key: K): ValueOfKey<T, K> => {
  if (!isObject(value)) return undefined as ValueOfKey<T, K>;

  return value[key] as ValueOfKey<T, K>;
};

export const deleteUndefined = <T extends object>(value: T): T =>
  keys(value).reduce((result, key) => {
    if (value[key] !== undefined) result[key] = value[key];
    return result;
  }, {} as T);

type PickedFromUnion<T> = {
  [P in keyof UnionToIntersection<T>]-?: NonNullable<UnionToIntersection<T>[P]>;
};

export const pickFromUnion = <T, K extends PropertyKey>(
  value: T,
  _keys: K[]
): PickedFromUnion<T> => value as unknown as PickedFromUnion<T>;

export const unpackProps = <T extends object, K extends keyof T>(
  props: T,
  unpackKeys: K[]
): { unpacked: Pick<T, K>; rest: Omit<T, K> } => {
  const unpacked = {} as Pick<T, K>;
  const rest = { ...props } as Omit<T, K> & Partial<Pick<T, K>>;

  unpackKeys.forEach((key) => {
    if (key in props) unpacked[key] = props[key];
    delete rest[key];
  });

  return { unpacked, rest: rest as Omit<T, K> };
};
